/**
 * Typography Components - Token-Based Typography System
 * 
 * CANON v1.5.0 - Single Source of Truth for Typography (SSOT)
 * 
 * These components enforce consistent typography across the platform.
 * All text should use these components instead of raw Tailwind classes.
 * 
 * SSOT ARCHITECTURE:
 * ┌─────────────────────────────────────────────────────────────┐
 * │  1. tailwind.config.ts (fontSize scale)                     │
 * │     └── hero-*, display-*, heading-*, body-*, caption-*     │
 * ├─────────────────────────────────────────────────────────────┤
 * │  2. packages/config/theme.ts (semantic tokens)              │
 * │     └── semanticTokens.typography.hero*, display*, h*, etc  │
 * ├─────────────────────────────────────────────────────────────┤
 * │  3. typography.tsx (React components)                       │
 * │     └── <Display>, <Heading>, <Text>, <Numbers>, <Code>     │
 * └─────────────────────────────────────────────────────────────┘
 * 
 * COMPONENT TYPES:
 * - Display: Satoshi Black (hero, impact) - hero scale OR display scale
 * - Heading: Satoshi Bold/SemiBold (titles) - 32px → 14px (h1-h6)
 * - Text: Inter Regular (body) - 18px → 11px
 * - Numbers: Inter Tabular (money, stats) - with tabular-nums
 * 
 * DISPLAY SCALES:
 * - Hero scale: For 2-column layouts (36px → 76px) - fits "Automatically."
 * - Display scale: For full-width (30px → 128px) - maximum impact
 * 
 * Usage:
 *   <Display variant="hero">Landing Hero</Display>  // 2-column layout
 *   <Display variant="3xl">Full-width Hero</Display> // Full-width
 *   <Heading level={1}>Page Title</Heading>
 *   <Text variant="lg">Lead paragraph</Text>
 *   <Numbers variant="display">${amount}</Numbers>
 * 
 * TEXT WRAPPING:
 * - <Text> and <Heading> automatically include 'break-words' to prevent text overflow
 * - Use in containers with constrained width (cards, grids, flex layouts)
 * - For single-line truncation, use truncate prop
 * - For multi-line clamping, use lineClamp prop
 * 
 * @version 1.5.1
 * @updated 2026-01-08
 */

import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

// ============================================
// DISPLAY COMPONENT
// For hero text, landing pages, impact statements
// Uses: Satoshi Black
// ============================================

type DisplayVariant = 'hero' | '3xl' | '2xl' | 'xl' | 'lg' | 'md' | 'sm';

const displayVariants: Record<DisplayVariant, string> = {
  // ============================================
  // HERO: Token-based responsive scale for 2-column layouts
  // CANON v1.5.0: ZERO ARBITRARY VALUES - uses SSOT tokens only
  // ============================================
  // Source: tailwind.config.ts → hero-* scale
  // Source: theme.ts → semanticTokens.typography.hero*
  // Fits: "Send Crypto. Get Cash. Automatically." at 50% width
  // ============================================
  hero: [
    semanticTokens.typography.heroXs,              // Base: 36px (mobile 375px)
    'sm:text-hero-sm',                             // SM: 44px (640px)
    'md:text-hero-md',                             // MD: 52px (768px)
    'lg:text-hero-lg',                             // LG: 60px (1024px)
    'xl:text-hero-xl',                             // XL: 68px (1280px)
    '2xl:text-hero-2xl',                           // 2XL: 76px (1400px)
  ].join(' '),
  
  // Full-width display variants (for marketing pages without side content)
  '3xl': semanticTokens.typography.display3xl,
  '2xl': semanticTokens.typography.display2xl,
  xl: semanticTokens.typography.displayXl,
  lg: semanticTokens.typography.displayLg,
  md: semanticTokens.typography.displayMd,
  sm: semanticTokens.typography.displaySm,
};

export interface DisplayProps extends HTMLAttributes<HTMLDivElement> {
  /** Display size variant */
  variant?: DisplayVariant;
  /** Content */
  children: ReactNode;
  /** HTML tag to render (default: div) */
  as?: 'div' | 'span' | 'p' | 'h1' | 'h2';
}

export const Display = forwardRef<HTMLDivElement, DisplayProps>(
  ({ variant = 'lg', children, className, as: Tag = 'div', ...props }, ref) => {
    return (
      <Tag
        ref={ref}
        className={cn(
          displayVariants[variant],
          // Color inherits from parent or CSS var(--foreground)
          className
        )}
        {...props}
      >
        {children}
      </Tag>
    );
  }
);
Display.displayName = 'Display';

// ============================================
// HEADING COMPONENT
// For page titles, section headlines, card titles
// Uses: Satoshi Bold/SemiBold
// 
// CANON v1.4.0: 4-level visual hierarchy
// - H1: 32px - Page titles
// - H2: 24px - Section headlines
// - H3: 20px - Subsection headlines
// - H4: 18px - Card titles (also used for h5/h6)
// 
// Note: h5/h6 map to h4 styling because 4 levels
// is sufficient for most UI hierarchies. If you need
// more granularity, use Text with variant="label".
// ============================================

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

const headingVariants: Record<HeadingLevel, string> = {
  1: semanticTokens.typography.h1,    // 32px - Page titles
  2: semanticTokens.typography.h2,    // 24px - Section headlines
  3: semanticTokens.typography.h3,    // 20px - Subsection headlines
  4: semanticTokens.typography.h4,    // 18px - Card titles
  5: semanticTokens.typography.h4,    // 18px - Alias to h4 (visual hierarchy limit)
  6: semanticTokens.typography.h4,    // 18px - Alias to h4 (visual hierarchy limit)
};

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  /** Heading level (1-6) - determines both tag and styling */
  level: HeadingLevel;
  /** Content */
  children: ReactNode;
  /** Override the HTML tag (useful for SEO while keeping visual style) */
  as?: HeadingTag;
}

export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ level, children, className, as, ...props }, ref) => {
    const Tag = as || (`h${level}` as HeadingTag);
    
    return (
      <Tag
        ref={ref}
        className={cn(
          headingVariants[level],
          // Color inherits from parent or CSS var(--foreground)
          'break-words', // Prevent text overflow in constrained containers
          className
        )}
        {...props}
      >
        {children}
      </Tag>
    );
  }
);
Heading.displayName = 'Heading';

// ============================================
// TEXT COMPONENT
// For body text, paragraphs, descriptions
// Uses: Inter Regular/Medium
// ============================================

type TextVariant = 'lg' | 'md' | 'sm' | 'caption' | 'caption-sm' | 'label';

const textVariants: Record<TextVariant, string> = {
  lg: semanticTokens.typography.bodyLarge,
  md: semanticTokens.typography.body,
  sm: semanticTokens.typography.bodySmall,
  caption: semanticTokens.typography.caption,
  'caption-sm': semanticTokens.typography.captionSmall,
  label: semanticTokens.typography.label,
};

type TextColor = 'default' | 'muted' | 'subtle' | 'inverted';

const textColors: Record<TextColor, string> = {
  default: semanticTokens.fg.default,
  muted: semanticTokens.fg.muted,
  subtle: semanticTokens.fg.subtle,
  inverted: semanticTokens.fg.inverted,
};

export interface TextProps extends HTMLAttributes<HTMLParagraphElement> {
  /** Text size variant */
  variant?: TextVariant;
  /** Text color */
  color?: TextColor;
  /** Content */
  children: ReactNode;
  /** HTML tag to render (default: p for body, span for label/caption) */
  as?: 'p' | 'span' | 'div' | 'label';
  /** Truncate text with ellipsis (single line) */
  truncate?: boolean;
  /** Clamp text to N lines (2 or 3) */
  lineClamp?: 2 | 3;
}

export const Text = forwardRef<HTMLParagraphElement, TextProps>(
  ({ variant = 'md', color = 'default', children, className, as, truncate, lineClamp, ...props }, ref) => {
    // Default tag based on variant
    const defaultTag = variant === 'label' || variant === 'caption' || variant === 'caption-sm' 
      ? 'span' 
      : 'p';
    const Tag = as || defaultTag;
    
    return (
      <Tag
        ref={ref}
        className={cn(
          textVariants[variant],
          // Only add color class if explicitly set (not 'default')
          color !== 'default' && textColors[color],
          // Text wrapping: break-words по умолчанию для предотвращения overflow
          'break-words',
          // Truncation utilities
          truncate && 'truncate',
          lineClamp === 2 && 'line-clamp-2',
          lineClamp === 3 && 'line-clamp-3',
          className
        )}
        {...props}
      >
        {children}
      </Tag>
    );
  }
);
Text.displayName = 'Text';

// ============================================
// LEAD COMPONENT
// For lead paragraphs, introductions
// Convenience wrapper around Text with lg variant
// ============================================

export type LeadProps = Omit<TextProps, 'variant'>;

export const Lead = forwardRef<HTMLParagraphElement, LeadProps>(
  ({ children, color = 'muted', className, ...props }, ref) => {
    return (
      <Text
        ref={ref}
        variant="lg"
        color={color}
        className={cn('max-w-prose', className)}
        {...props}
      >
        {children}
      </Text>
    );
  }
);
Lead.displayName = 'Lead';

// ============================================
// CODE COMPONENT
// For inline code snippets
// Uses: Monospace font
// ============================================

export interface CodeProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
}

export const Code = forwardRef<HTMLElement, CodeProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <code
        ref={ref}
        className={cn(
          semanticTokens.typography.code,
          'px-1.5 py-0.5 rounded',
          semanticTokens.bg.muted,
          // Color inherits from parent or CSS var(--foreground)
          className
        )}
        {...props}
      >
        {children}
      </code>
    );
  }
);
Code.displayName = 'Code';

// ============================================
// NUMBERS COMPONENT
// For tabular numbers, prices, statistics
// Uses: Inter with tabular-nums
// 
// CANON v1.4.0 Updates:
// - Added tracking-tight for large numbers (better visual density)
// - Added explicit leading for consistent line-height
// - Mobile-first responsive scaling
// ============================================

type NumbersVariant = 'xl' | 'lg' | 'md' | 'sm' | 'xs' | 'display';

const numbersVariants: Record<NumbersVariant, string> = {
  // Display: Hero-level numbers (portfolio totals, big stats)
  // Mobile: 36px → SM: 48px → MD: 60px
  display: [
    'font-display',
    'text-4xl sm:text-5xl md:text-6xl',  // 36px → 48px → 60px
    'font-bold',
    'tabular-nums',
    'tracking-tight',     // Tighter spacing for large numbers
    'leading-tight',      // 1.25 line-height
  ].join(' '),
  
  // XL: Large transaction amounts
  // Mobile: 30px → SM: 36px
  xl: [
    'font-display',
    'text-3xl sm:text-4xl',              // 30px → 36px
    'font-bold',
    'tabular-nums',
    'tracking-tight',
    'leading-tight',
  ].join(' '),
  
  // LG: Card-level amounts (asset cards, transaction cards)
  // Mobile: 24px → SM: 30px
  lg: [
    'font-body',
    'text-2xl sm:text-3xl',              // 24px → 30px
    'font-semibold',
    'tabular-nums',
    'tracking-tight',
    'leading-snug',       // 1.375 line-height
  ].join(' '),
  
  // MD: Inline amounts (transaction rows, lists)
  md: [
    'font-body',
    'text-lg',                           // 18px
    'font-medium',
    'tabular-nums',
    'leading-normal',     // 1.5 line-height
  ].join(' '),
  
  // SM: Secondary amounts (fees, subtotals)
  sm: [
    'font-body',
    'text-sm',                           // 14px
    'font-medium',
    'tabular-nums',
    'leading-normal',
  ].join(' '),
  
  // XS: Micro amounts (timestamps with values, badges)
  xs: [
    'font-body',
    'text-xs',                           // 12px
    'font-medium',
    'tabular-nums',
    'leading-normal',
  ].join(' '),
};

export interface NumbersProps extends HTMLAttributes<HTMLSpanElement> {
  /** Size variant */
  variant?: NumbersVariant;
  /** Content (numbers, currency, etc.) */
  children: ReactNode;
}

export const Numbers = forwardRef<HTMLSpanElement, NumbersProps>(
  ({ variant = 'md', children, className, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          numbersVariants[variant],
          // Color inherits from parent or CSS var(--foreground)
          className
        )}
        {...props}
      >
        {children}
      </span>
    );
  }
);
Numbers.displayName = 'Numbers';

// ============================================
// TEXT UTILITIES
// Common text manipulation patterns
// ============================================

export const textUtilities = {
  /** Single line truncate with ellipsis */
  truncate: 'truncate',
  /** Clamp to 2 lines with ellipsis */
  lineClamp2: 'line-clamp-2',
  /** Clamp to 3 lines with ellipsis */
  lineClamp3: 'line-clamp-3',
  /** Prevent text wrapping */
  noWrap: 'whitespace-nowrap',
  /** Break words to prevent overflow */
  breakWords: 'break-words',
  /** Balance text for headlines */
  balance: 'text-balance',
} as const;

// ============================================
// EXPORTS
// ============================================

export type {
  DisplayVariant,
  HeadingLevel,
  TextVariant,
  TextColor,
  NumbersVariant,
};

